/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetProjectRequest,
  GetProjectSecurity,
} from "../models/operations/getproject.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildProjectQuery,
  prefetchProject,
  ProjectQueryData,
  queryKeyProject,
} from "./project.core.js";
export {
  buildProjectQuery,
  prefetchProject,
  type ProjectQueryData,
  queryKeyProject,
};

export type ProjectQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getProject projects
 *
 * @remarks
 * Get project details by slug.
 */
export function useProject(
  request: GetProjectRequest,
  security?: GetProjectSecurity | undefined,
  options?: QueryHookOptions<ProjectQueryData, ProjectQueryError>,
): UseQueryResult<ProjectQueryData, ProjectQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildProjectQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getProject projects
 *
 * @remarks
 * Get project details by slug.
 */
export function useProjectSuspense(
  request: GetProjectRequest,
  security?: GetProjectSecurity | undefined,
  options?: SuspenseQueryHookOptions<ProjectQueryData, ProjectQueryError>,
): UseSuspenseQueryResult<ProjectQueryData, ProjectQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildProjectQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setProjectData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      slug: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ProjectQueryData,
): ProjectQueryData | undefined {
  const key = queryKeyProject(...queryKeyBase);

  return client.setQueryData<ProjectQueryData>(key, data);
}

export function invalidateProject(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      slug: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "projects", "read", ...queryKeyBase],
  });
}

export function invalidateAllProject(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "projects", "read"],
  });
}
